<script lang="ts">
import Breadcrumb from './breadcrumb.vue';
import RequestHeader from '@/components/pages/complaint/detail/header.vue';
import RequestInfomation from '@/components/pages/complaint/detail/information.vue';
import UserCancel from '@/components/pages/complaint/detail/userCancel.vue';
import UserResend from '@/components/pages/complaint/detail/userResend.vue';
import UserAttachment from '@/components/pages/admin/complaint-list/shared/attachment.vue';
import { ComplaintAdditionalDoc, ComplaintInfo } from '@/interface/Complaint.interface';
import { complaintRequestBreadcrumb } from '@/components/utils';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ADD_DOCS_STATUS, LANG_EN, REQUEST_STATUS, USER_BTN_KEY } from '@/app/const';
import { complaintService, fileService, masterService } from '@/services/serviceProvider';
import { groupBy } from '@/app/common/types/groupBy.type';
import { AttachmentUploaderType, AttachmentUrl } from '@/app/common/types/attachUploader';
import { UserEndReason } from '@/interface/UserEndReason.interface';
import { UploadResponse } from '@/interface/FileUpload.interface';
import RequestAppeal from '@/components/pages/complaint/detail/appeal.vue';

export default {
    components: {
        Breadcrumb,
        RequestHeader,
        RequestInfomation,
        UserCancel,
        UserResend,
        UserAttachment,
        RequestAppeal
    },
    setup() {
        const router = useRouter();
        const currentRoute = router.currentRoute.value;
        const step = ref(1);
        // components
        const flight = ref();
        const passenger = ref();
        const topic = ref();

        return { router, currentRoute, step, flight, passenger, topic }
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
    },
    emits: [
        'update:modelValue',
        'displayConfirmDialog',
        'displayAlertDialog'
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            breadcrumb: [...complaintRequestBreadcrumb],
            displayUserCancelModal: false,
            displayUserResendModal: false,
            userEndReasons: [] as UserEndReason[],
            lang: this.$i18n.locale,
        }
    },
    watch: {
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        isNew() {
            return this.currentRoute.params.requestNo === undefined;
        },
        allAdditionalDocs() {
            const ascDocs = this.model.ascDepartment?.additionalDocs || [];
            const mainDpmDocs = this.model.relatedMainDepartment?.additionalDocs || [];
            let subDpmDocs = [] as ComplaintAdditionalDoc[];
            this.model.relatedSubDepartments?.forEach(o => {
                const docs = o.additionalDocs || [];
                if (docs && docs.length > 0) {
                    subDpmDocs = subDpmDocs.concat(docs);
                }
            })
            return ascDocs.concat(mainDpmDocs).concat(subDpmDocs)
                ?.filter(o => o.status === ADD_DOCS_STATUS.APPROVED
                    || o.status === ADD_DOCS_STATUS.UPLOADED
                    || o.status === ADD_DOCS_STATUS.COMPLETED);
        },
        canUpload(): boolean {
            return this.model.status === REQUEST_STATUS.REQUEST_DOC
        },
        enableAttachment(): boolean {
            return this.model.status === REQUEST_STATUS.REQUEST_DOC
                || (this.model.status !== REQUEST_STATUS.DRAFT && this.allAdditionalDocs.length > 0);
        },
        enableAscEndReason(): boolean {
            return this.model.status === REQUEST_STATUS.END
                && this.model.ascEndReason !== undefined
                && this.model.ascEndReason !== null;
        },
        enableUserEndReason(): boolean {
            return this.model.status === REQUEST_STATUS.END
                && (this.model.ascEndReason === undefined
                    || this.model.ascEndReason === null);
        },
        filteredBreadcrumb() {
            switch (this.model.status) {
                case REQUEST_STATUS.ACTIVE:
                case REQUEST_STATUS.IN_PROGRESS:
                    return this.breadcrumb.filter(o => o.key === USER_BTN_KEY.CANCEL);
                case REQUEST_STATUS.REQUEST_DOC:
                    return this.breadcrumb.filter(o => o.key === USER_BTN_KEY.CANCEL
                        || o.key === USER_BTN_KEY.SAVE
                    );
                case REQUEST_STATUS.END:
                    if (this.model.ascEndReason && !this.model.refRequestNo)
                        return this.breadcrumb.filter(o => o.key === USER_BTN_KEY.RESEND);
                case REQUEST_STATUS.CANCELLED:
                default:
                    return [];
            }
        }
    },
    async mounted() {
        this.getUserEndReasons();
    },
    methods: {
        async getUserEndReasons() {
            try {
                const res = await masterService.getEndReasons();
                if (res && res.status === 200) {
                    this.userEndReasons = res.data as UserEndReason[];
                }
            } catch (e) {
            }
        },
        async endRequest(complaint: ComplaintInfo) {
            try {
                if (this.model.requestNo) {
                    const res = complaintService.userEndRequest(this.model.requestNo, {
                        reasonId: complaint.userEndReasonId,
                        reasonOther: complaint.userEndReason ?? null
                    });
                    if (res && (await res).status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-cancel-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-cancel-fail'),
                } as BaseAlert)
            }
        },
        async uploadFileBeforeSave() {
            try {
                let allUploads = [] as AttachmentUploaderType[];
                for (const doc of this.allAdditionalDocs) {
                    if (doc.fileUploads && doc.fileUploads.length > 0) {
                        if (!doc.attachments) doc.attachments = [];
                        allUploads = allUploads.concat([...doc.fileUploads]);
                    }
                }

                if (allUploads && allUploads.length > 0) {
                    // prepare payloads
                    let payloads = [] as File[];
                    allUploads.forEach(attach => {
                        if (attach.blob) {
                            const blob = fileService.dataURLToBlob(attach.blob);
                            let file = new File([blob], attach.name, { type: attach.fileType })
                            payloads.push(file);
                        }
                    });
                    // call upload API
                    const res = await fileService.upload(payloads);
                    if (res && res.status === 201) {
                        const data = res.data as UploadResponse;
                        data.files.forEach(file => {
                            let attachment = {
                                itemNo: -1,
                                fileName: file.fileName,
                                runningNo: -1,
                                size: allUploads?.find(upload => upload.name === file.fileName)?.size,
                                url: file.objectName,
                            } as AttachmentUrl;

                            // add attachments for save
                            let doc = undefined as ComplaintAdditionalDoc | undefined;
                            this.allAdditionalDocs?.forEach(o => {
                                let uploadedFile = o.fileUploads?.find(f => f.name === attachment.fileName);
                                if (uploadedFile !== undefined) {
                                    doc = o;
                                    return;
                                }
                            });
                            if (doc) {
                                doc.attachments?.push(attachment);
                            }
                        });

                        // clear file uploads
                        this.allAdditionalDocs.forEach(o => {
                            o.fileUploads = [];
                        })

                    } else {
                        throw new Error();
                    }
                }
            } catch (e: any) {
                throw new Error("Upload Error");
            }
        },
        async submitAdditionalDocs() {
            try {
                if (this.model.requestNo) {
                    await this.uploadFileBeforeSave()
                    const grouped = groupBy(this.allAdditionalDocs, ['departmentCode', 'departmentType'])
                    let payloads = [] as any[];
                    Object.entries(grouped).forEach(([department, docs]) => {
                        const dpm = department.split('_');
                        docs.forEach(d => {
                            payloads.push({
                                departmentCode: d.departmentCode,
                                departmentType: d.departmentType,
                                docTypeNo: d.docTypeNo,
                                attachments: d.attachments
                            })
                        })
                    });
                    const res = complaintService.userSendDocs(this.model.requestNo, payloads);
                    if (res && (await res).status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-send-additional-doc-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e: any) {
                if (e.message === 'Upload Error') {
                    this.displayAlertDialog({
                        display: true,
                        success: false,
                        messageTitle: this.$t('t-upload-file-fail'),
                    } as BaseAlert)
                } else {
                    this.displayAlertDialog({
                        display: true,
                        success: false,
                        messageTitle: this.$t('t-action-send-additional-doc-fail'),
                    } as BaseAlert)
                }
            }
        },
        async appealRequest(complaint: ComplaintInfo) {
            try {
                if (this.model.requestNo) {
                    const res = complaintService.userAppeal(this.model.requestNo, {
                        reason: complaint.appealReason
                    });
                    if (res && (await res).status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-resend-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-resend-fail'),
                } as BaseAlert)
            }
        },
        goToListPage() {
            this.router.push({
                path: `/complaint-status`
            })
        },
        goPrevPage() {
            this.router.go(-1);
        },
        preSubmitAdditionalDocs() {
            if (this.allAdditionalDocs.filter(o => o.status === ADD_DOCS_STATUS.APPROVED && (o.fileUploads && o.fileUploads.length > 0))?.length 
                === this.allAdditionalDocs.filter(o => o.status === ADD_DOCS_STATUS.APPROVED).length) {
                this.displayConfirmDialog({
                    display: true,
                    messageTitle: this.$t('t-action-send-additional-doc-confirm'),
                    callBack: this.submitAdditionalDocs
                } as BaseConfirm)
            } else {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-send-additional-doc-missing'),
                } as BaseAlert)
            }
        },
        displayConfirmDialog(model: BaseConfirm) {
            this.$emit('displayConfirmDialog', model);
        },
        displayAlertDialog(model: BaseAlert) {
            this.$emit('displayAlertDialog', model);
        },
        userEndReasonText() {
            const endReason = this.userEndReasons.find(o => o.id === this.model.userEndReasonId)
            return this.lang === LANG_EN ? endReason?.reasonEn : endReason?.reasonTh;
        }
    },
};
</script>

<template>
    <div class="bg-page-home main-page"></div>
    <div class="bg-page-home-content complaint-detail app-form-input">
        <Breadcrumb :title="$t('t-complaint')" v-model="filteredBreadcrumb"
            @do-cancel-request="displayUserCancelModal = true" @do-send-request-again="displayUserResendModal = true"
            @do-user-save="preSubmitAdditionalDocs" />
        <RequestHeader v-if="!isNew && model" v-model="model"></RequestHeader>
        <RequestAppeal v-if="!isNew && model" v-model="model"></RequestAppeal>
        <UserAttachment v-if="enableAttachment" v-model="allAdditionalDocs" :view-mode="!canUpload">
        </UserAttachment>
        <v-card v-if="enableAscEndReason" class="app-card">
            <v-toolbar density="compact">
                <v-toolbar-title>{{ $t('t-complaint-answer-back-info') }}</v-toolbar-title>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text>
                <v-textarea v-model.trim="model.ascEndReason" class="hide-float-label" variant="outlined"
                    hide-details="auto" density="comfortable" auto-grow readonly>
                </v-textarea>
                <div class="textarea-export formatted-new-line mt-2" style="display: none;">
                    {{ model.ascEndReason ?? '-' }}
                </div>
            </v-card-text>
        </v-card>
        <v-card v-if="enableUserEndReason" class="app-card">
            <v-toolbar density="compact">
                <v-toolbar-title>{{ $t('t-complaint-end-reason') }}</v-toolbar-title>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text>
                <v-textarea v-if="!model.userEndReasonId || model.userEndReasonId === 99"
                    v-model.trim="model.userEndReason" class="hide-float-label" variant="outlined" hide-details="auto"
                    density="comfortable" auto-grow readonly>
                </v-textarea>
                <v-textarea v-else :value="userEndReasonText()" class="hide-float-label" variant="outlined"
                    hide-details="auto" density="comfortable" auto-grow readonly>
                </v-textarea>
                <div v-if="!model.userEndReasonId || model.userEndReasonId === 99"
                    class="textarea-export formatted-new-line mt-2" style="display: none;">
                    {{ model.userEndReason ?? '-' }}
                </div>
                <div v-else class="textarea-export formatted-new-line mt-2" style="display: none;">
                    {{ userEndReasonText() ?? '-' }}
                </div>
            </v-card-text>
        </v-card>
        <RequestInfomation v-model="model"></RequestInfomation>
        <UserCancel v-model="displayUserCancelModal" @on-confirm="endRequest"></UserCancel>
        <UserResend v-model="displayUserResendModal" @on-confirm="appealRequest"></UserResend>
    </div>
</template>